/**
 * Founder Control Center v2 · Operations · Leads platform inbox.
 *
 * Bandeja platform-wide de leads recibidos vía widgets/marketplace, accesible
 * solo al SuperAdmin/Founder. Reutiliza los endpoints ya existentes:
 *
 *   GET  /api/superadmin/leads?status=&limit=  → {total, items, by_status}
 *   POST /api/superadmin/leads/{id}/status     → {status: new|read|replied|spam}
 *
 * 0 backend tocado · 0 endpoints nuevos · 0 mocks. Sin dependencia SuperAdmin.
 *
 * Nota: en V2 spec §5.1 figura como `/founder/operations/leads` y es uno de los
 * 6 módulos obligatorios del Founder Control Center (lista del usuario Fase 5).
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { Tray, ArrowsClockwise, EnvelopeSimple, CheckCircle, X, ChatCircleDots, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "",         label: "Todos" },
  { key: "new",      label: "Nuevos" },
  { key: "read",     label: "Leídos" },
  { key: "replied",  label: "Respondidos" },
  { key: "spam",     label: "Spam" },
];

const STATUS_TONES = {
  new:      "bg-[var(--ag-gold)]/15 text-[var(--ag-gold)] border-[var(--ag-gold)]/40",
  read:     "bg-blue-500/15 text-blue-300 border-blue-500/40",
  contacted:"bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  replied:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  won:      "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  lost:     "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  spam:     "bg-red-500/15 text-red-300 border-red-500/40",
};

export default function LeadsAdminPanel() {
  const [filter, setFilter] = useState("");
  const [data, setData] = useState({ total: 0, items: [], by_status: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const load = async (status = filter) => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get(`/superadmin/leads?status=${encodeURIComponent(status)}&limit=100`);
      setData(data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "No se pudieron cargar los leads");
    } finally { setLoading(false); }
  };

  // Un único useEffect dependiente de `filter` (valor inicial "" cubre el mount).
  useEffect(() => { load(filter); }, [filter]);

  const setStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await api.post(`/superadmin/leads/${id}/status`, { status });
      toast.success(`Lead marcado como ${status}`);
      await load(filter);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "No se pudo cambiar el estado");
    } finally { setUpdatingId(null); }
  };

  const tabCounts = useMemo(() => {
    const t = { "": data.total };
    Object.entries(data.by_status || {}).forEach(([k, v]) => { t[k] = v; });
    return t;
  }, [data]);

  return (
    <div className="space-y-4" data-testid="leads-admin-root">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <Tray size={11} weight="fill" /> Operations · Leads platform
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[var(--ag-text)] mt-1">
            Inbox de leads de toda la plataforma
          </h2>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            Leads recogidos por widgets · marketplace · formularios públicos. Vista cross-org. Estado: <code>new → read → replied/spam</code>.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(filter)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50 disabled:opacity-50"
          data-testid="leads-refresh"
        >
          <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refrescar
        </button>
      </header>

      <div className="flex flex-wrap gap-1.5" data-testid="leads-filter-tabs">
        {STATUS_TABS.map((s) => (
          <button
            key={s.key || "all"}
            type="button"
            onClick={() => setFilter(s.key)}
            data-testid={`leads-filter-${s.key || "all"}`}
            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${
              filter === s.key
                ? "bg-[var(--ag-gold)] text-black border-transparent font-bold"
                : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"
            }`}
          >
            {s.label}
            <span className="tabular-nums opacity-80">({tabCounts[s.key] ?? 0})</span>
          </button>
        ))}
      </div>

      {err && (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2 inline-flex items-center gap-2" data-testid="leads-error">
          <Warning size={14} /> {err}
        </div>
      )}

      {loading && !data.items.length && (
        <div className="text-xs text-[var(--ag-text-dim)]">Cargando leads…</div>
      )}

      {!loading && !err && data.items.length === 0 && (
        <div className="rounded border border-dashed border-[var(--ag-border)] p-8 text-center text-sm text-[var(--ag-text-dim)]" data-testid="leads-empty">
          Sin leads {filter ? `en estado "${filter}"` : "todavía"}. Asegúrate de que los widgets de captación estén activos.
        </div>
      )}

      {data.items.length > 0 && (
        <div className="overflow-x-auto rounded border border-[var(--ag-border)]" data-testid="leads-table-wrap">
          <table className="w-full text-xs">
            <thead className="bg-[var(--ag-midnight)]/50 text-[10px] uppercase text-[var(--ag-text-muted)]">
              <tr>
                <th className="text-left px-3 py-2">Cuándo</th>
                <th className="text-left px-3 py-2">Centro</th>
                <th className="text-left px-3 py-2">Lead</th>
                <th className="text-left px-3 py-2">Contacto</th>
                <th className="text-left px-3 py-2">Mensaje</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-left px-3 py-2 w-1">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.id} className="border-t border-[var(--ag-border)] hover:bg-[var(--ag-surface)]/40" data-testid={`lead-row-${l.id}`}>
                  <td className="px-3 py-2 text-[var(--ag-text-dim)] tabular-nums">
                    {l.created_at ? new Date(l.created_at).toLocaleString("es-ES") : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-[var(--ag-text)]">{l.org_name || "—"}</div>
                    <div className="text-[10px] text-[var(--ag-text-muted)] font-mono">{l.org_slug || l.org_id?.slice(0, 8)}</div>
                  </td>
                  <td className="px-3 py-2 text-[var(--ag-text)]">{l.name || "—"}</td>
                  <td className="px-3 py-2 text-[var(--ag-text-dim)]">
                    <div className="font-mono">{l.phone || "—"}</div>
                    {l.email && <div className="font-mono text-[10px]">{l.email}</div>}
                  </td>
                  <td className="px-3 py-2 text-[var(--ag-text-dim)] max-w-xs truncate" title={l.message}>
                    {l.service && <span className="text-[var(--ag-gold)] font-semibold mr-1">[{l.service}]</span>}
                    {l.message || "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_TONES[l.status] || "border-[var(--ag-border)] text-[var(--ag-text-dim)]"}`}>
                      {l.status || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex gap-1">
                      <ActionBtn
                        icon={EnvelopeSimple}
                        title="Marcar leído"
                        disabled={updatingId === l.id || l.status === "read"}
                        onClick={() => setStatus(l.id, "read")}
                        testid={`lead-action-read-${l.id}`}
                      />
                      <ActionBtn
                        icon={CheckCircle}
                        title="Marcar respondido"
                        disabled={updatingId === l.id || l.status === "replied"}
                        onClick={() => setStatus(l.id, "replied")}
                        testid={`lead-action-replied-${l.id}`}
                        accent="ok"
                      />
                      <ActionBtn
                        icon={X}
                        title="Marcar spam"
                        disabled={updatingId === l.id || l.status === "spam"}
                        onClick={() => setStatus(l.id, "spam")}
                        testid={`lead-action-spam-${l.id}`}
                        accent="danger"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-3 text-[10px] text-[var(--ag-text-muted)] pt-2 border-t border-[var(--ag-border)]">
        <ChatCircleDots size={11} />
        <span>Total cargados: <strong>{data.items.length}</strong> · global: <strong>{data.total}</strong> · fuente: <code>/api/superadmin/leads</code></span>
      </div>
    </div>
  );
}

function ActionBtn({ icon: Icon, title, onClick, disabled, accent, testid }) {
  const cls = accent === "ok"
    ? "border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
    : accent === "danger"
    ? "border-red-500/30 text-red-300 hover:bg-red-500/10"
    : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] hover:border-[var(--ag-gold)]/50";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center gap-1 p-1.5 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}
      data-testid={testid}
    >
      <Icon size={12} />
    </button>
  );
}
